<template>
  <div v-if="pagination.Total > pagination.Take" class="pagination">
    <div class="numbers">
      <span
        v-for="(num, idx) in paginationRange"
        :key="idx"
        :class="[
          pagination.Page == num ? 'active' : '',
          typeof num == 'number' ? 'number-item' : 'string-item',
        ]"
        @click="onPageChange(num)"
      >
        {{ num }}
      </span>
    </div>
    <div class="arrows">
      <button
        type="button"
        class="icon"
        :class="pagination.Page == 1 ? 'disabled' : ''"
        @click="pagination.Page > 1 ? onPageChange(pagination.Page - 1) : ''"
      >
        <i class="fa-solid fa-caret-left" />
      </button>
      <button
        type="button"
        class="icon"
        :class="pagination.Page == paginationRange[paginationRange.length - 1] ? 'disabled' : ''"
        @click="pagination.Page < paginationRange.length ? onPageChange(pagination.Page + 1) : ''"
      >
        <i class="fa-solid fa-caret-right" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'

  import type { IUIPagination } from '@/types/Components'

  const emit = defineEmits(['pageChange'])

  const { pagination } = defineProps<IUIPagination>()

  const paginationRange = computed(() => {
    const total = pagination.Total
    const pageCount = Math.ceil(total / pagination.Take)

    const current = pagination.Page
    const last = pageCount
    const delta = 2
    const left = current - delta
    const right = current + delta + 1
    const range: number[] = []
    const rangeWithDots: (number | string)[] = []
    let l: number | undefined = undefined

    for (let i = 1; i <= last; i++) {
      if (i === 1 || i === last || (i >= left && i < right)) {
        range.push(i)
      }
    }

    for (const i of range) {
      if (l !== undefined) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1)
        } else if (i - l !== 1) {
          rangeWithDots.push('...')
        }
      }
      rangeWithDots.push(i)
      l = i
    }

    return rangeWithDots
  })

  const onPageChange = (page: number | string) => {
    emit('pageChange', page)
  }
</script>

<style lang="scss" scoped>
  .pagination {
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin: 2rem 0 4rem 0;
    @include xs {
      margin: 1rem 0 2rem 0;
    }
    .arrows {
      display: flex;
      align-items: center;
      @include xs {
        display: none;
      }
      .icon {
        height: 40px;
        width: 50px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #dedede;
        cursor: pointer;
        border-radius: 5px;
        transition: 0.2s ease all;
        @include xs {
          height: 30px;
          width: 40px;
        }
        &:hover {
          border-color: #606060;
        }
        &.disabled {
          cursor: not-allowed;
          border-color: #dedede;
        }
        &:nth-child(1) {
          margin-right: 0.5rem;
        }
      }
    }
    .numbers {
      display: flex;
      justify-content: flex-start;
      .string-item {
        height: 40px;
        width: 50px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: -0.5rem;
        @include xs {
          height: 30px;
          width: 40px;
        }
      }
      .number-item {
        height: 40px;
        width: 50px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #dedede;
        background-color: #fff;
        font-size: 1.2rem;
        font-weight: 600;
        cursor: pointer;
        border-radius: 5px;
        transition: 0.2s ease all;
        @include xs {
          height: 30px;
          width: 40px;
          font-size: 1rem;
        }
        &.active {
          background-color: var(--text-color);
          color: white;
        }
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
        &:hover {
          border-color: #dedede;
        }
      }
    }
  }
</style>
